import React, { Suspense, useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import {
  Outlet,
  ScrollRestoration,
  useLocation,
} from 'react-router-dom';

import {
  accountHomeBaseUrl,
  cookieJar,
  useIovation,
  useMaintenanceMode,
  useTrackingApply,
} from '@consumer-frontend/nc-api';
import {
  iovationBlackboxAtom,
  NcApplyHeader,
  NcFooter,
  NcModal,
} from '@consumer-frontend/nc-components';
import {
  normalizeTrackedAction,
  TrackPageActionApplyT,
} from '@consumer-frontend/nc-utils';
import { Container } from '@consumer-frontend/shared-ui';

import { DevToolsAtomSubscriber } from './DevToolsAtomSubscriber';
import { analytics } from '../../utils/adobe-analytics';

import '@consumer-bootstrap/netcredit.scss';

const VITE_ENV = import.meta.env.VITE_ENV || 'development';

// Dynamic import to keep faker out of prod builds.
const LazyNcFormFiller = VITE_ENV !== 'production'
  ? React.lazy(() => import('@consumer-frontend/nc-form-filler').then((m) => ({ default: m.NcFormFiller })))
  : null
;

export type RootLayoutPropsT = {
  isProduction?: boolean;
};

export const RootLayout: React.FC<RootLayoutPropsT> = ({
  isProduction = VITE_ENV === 'production',
}) => {
  useMaintenanceMode();

  useIovation(iovationBlackboxAtom);

  const { trackPage } = useTrackingApply();

  const location = useLocation();

  // Track route changes with Adobe Analytics
  useEffect(
    () => {
      const applyUuid = cookieJar.getApplyUuid();
      const [accountId] = cookieJar.getAccountId();

      try {
        analytics.track('routingChange', { to_path: location.pathname });

        trackPage({
          ...({ account_id: accountId }),
          tracked_action: normalizeTrackedAction(location.pathname) as TrackPageActionApplyT,
          tracked_controller: 'reactRouter',
          tracked_url: `${accountHomeBaseUrl}${location.pathname}`,
        });
      } catch {
        // swallow tracking errors, no action needed on error
      }

      datadogRum.setUser({
        id: accountId ?? applyUuid,
      });
    },
    [location.pathname, trackPage],
  );

  return (
    <>
      <NcApplyHeader />

      <Container className="root-container d-flex apply">
        <Outlet />
      </Container>

      <NcFooter />
      <NcModal />
      <ScrollRestoration />

      {!isProduction && LazyNcFormFiller && (
        <Suspense fallback={null}>
          <LazyNcFormFiller />
        </Suspense>
      )}
      {!isProduction && <DevToolsAtomSubscriber />}
    </>
  );
};
